import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useParams } from 'react-router-dom';

import { getManualArticle, getManualManifest, searchManual } from './api';
import MarkdownArticle, {
  ArticleTableOfContents,
  parseHeadings,
  prepareMarkdown,
} from './MarkdownArticle';
import './manual.css';


const DRAWER_WIDTH = 310;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const EDITORIAL_WARNING_IDS = new Set([
  'draft_unverified',
  'read_only_guidance',
]);
const UI_COPY = {
  en: {
    appTitle: 'Kalopsia Backoffice Manual',
    articleContents: 'Article contents',
    backToHome: 'Return to manual home',
    clearSearch: 'Clear search',
    darkMode: 'Use dark mode',
    external: 'external',
    imageUnavailable: 'Image unavailable',
    landingLead: 'Start with orientation and reporting guidance, then use the read-only references for investigation.',
    lastReviewed: 'Last reviewed',
    lightMode: 'Use light mode',
    loadingArticle: 'Loading article…',
    loadingManual: 'Loading the protected manual…',
    manual: 'Manual',
    manualHome: 'Manual home',
    navigation: 'Manual navigation',
    navigationDrawer: 'Manual navigation drawer',
    next: 'Next',
    noResults: 'No matching articles.',
    notFoundMessage: 'This manual page is not available.',
    notFoundTitle: 'Manual page not found',
    onThisPage: 'On this page',
    openNavigation: 'Open manual navigation',
    previous: 'Previous',
    printArticle: 'Print article',
    relatedArticles: 'Related articles',
    result: 'result',
    results: 'results',
    searchError: 'Search is temporarily unavailable.',
    searchLabel: 'Search the manual',
    searchLoading: 'Searching…',
    searchResults: 'Manual search results',
    searchShort: 'Enter at least two characters.',
    skipToContent: 'Skip to manual content',
    tableScrollable: 'Scrollable table',
  },
};

function storedChoice(key, allowed, fallback) {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch (_error) {
    return fallback;
  }
}

function hasStoredAccessToken() {
  try {
    const session = JSON.parse(localStorage.getItem('apiUser') || 'null');
    return Boolean(session && typeof session.accessToken === 'string' && session.accessToken);
  } catch (_error) {
    return false;
  }
}

function viewError(kind) {
  if (kind === 'forbidden') {
    return {
      title: 'Manual access denied',
      message: 'Your authenticated account is not eligible to use this manual.',
    };
  }
  if (kind === 'not-found') {
    return {
      title: 'Article not found',
      message: 'This manual article is not available.',
    };
  }
  if (kind === 'session-expired') {
    return {
      title: 'Session expired',
      message: 'Sign in again to continue.',
    };
  }
  if (kind === 'invalid-content') {
    return {
      title: 'Manual content unavailable',
      message: 'The protected content could not be validated.',
    };
  }
  return {
    title: 'Manual temporarily unavailable',
    message: 'The protected manual service is disabled or temporarily unavailable.',
  };
}

function LoadingState({ label }) {
  return (
    <Box className="manual-state" role="status" aria-live="polite">
      <CircularProgress size={32} />
      <Typography>{label}</Typography>
    </Box>
  );
}

function ErrorState({ kind }) {
  const error = viewError(kind);
  return (
    <Box className="manual-state" role="alert">
      <Typography component="h1" variant="h4">{error.title}</Typography>
      <Typography>{error.message}</Typography>
    </Box>
  );
}

function Highlight({ text, query }) {
  const normalized = query.trim();
  if (!normalized) return text;
  const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escaped})`, 'ig'));
  return parts.map((part, index) => (
    part.toLocaleLowerCase() === normalized.toLocaleLowerCase()
      ? <mark key={`${part}-${index}`}>{part}</mark>
      : part
  ));
}

function ManualSearch({ copy, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [state, setState] = useState('idle');
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    const value = query.trim();
    if (value.length < 2) {
      setResults([]);
      setState(value.length ? 'short' : 'idle');
      return () => { active = false; };
    }

    setState('loading');
    const timeout = window.setTimeout(() => {
      searchManual(value)
        .then((response) => {
          if (!active) return;
          setResults(Array.isArray(response.results) ? response.results : []);
          setState('complete');
        })
        .catch(() => {
          if (!active) return;
          setResults([]);
          setState('error');
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [query]);

  return (
    <Box className="manual-search manual-no-print">
      <TextField
        fullWidth
        size="small"
        label={copy.searchLabel}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        inputRef={inputRef}
        inputProps={{ 'aria-describedby': 'manual-search-status' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                aria-label={copy.clearSearch}
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Typography id="manual-search-status" className="manual-search-status" aria-live="polite">
        {state === 'loading' && copy.searchLoading}
        {state === 'short' && copy.searchShort}
        {state === 'complete' && `${results.length} ${results.length === 1 ? copy.result : copy.results}.`}
        {state === 'error' && copy.searchError}
      </Typography>
      {query.trim().length >= 2 && state === 'complete' && (
        <List aria-label={copy.searchResults} className="manual-search-results">
          {results.length === 0 ? (
            <Typography className="manual-empty-search">{copy.noResults}</Typography>
          ) : results.map((result) => (
            <ListItemButton
              key={result.slug}
              component={Link}
              to={`/manual/${result.slug}`}
              onClick={onNavigate}
            >
              <ListItemText
                primary={<Highlight text={result.title} query={query} />}
                secondary={(
                  <>
                    <span>{result.module}</span>
                    {result.matching_heading && <span> · {result.matching_heading}</span>}
                    <span className="manual-result-snippet"> {result.snippet}</span>
                  </>
                )}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
}

function Navigation({ copy, manifest, selectedSlug, onNavigate }) {
  const bySlug = useMemo(
    () => new Map(manifest.articles.map((article) => [article.slug, article])),
    [manifest.articles],
  );
  const activeModuleOrder = manifest.modules.find((module) => (
    module.article_slugs.includes(selectedSlug)
  ))?.navigation_order;
  const [expandedModules, setExpandedModules] = useState(() => {
    const initialModuleOrder = activeModuleOrder ?? manifest.modules[0]?.navigation_order;
    return new Set(initialModuleOrder === undefined ? [] : [initialModuleOrder]);
  });

  useEffect(() => {
    if (activeModuleOrder === undefined) return;
    setExpandedModules((current) => {
      if (current.has(activeModuleOrder)) return current;
      return new Set([...current, activeModuleOrder]);
    });
  }, [activeModuleOrder, selectedSlug]);

  const toggleModule = (moduleOrder) => {
    setExpandedModules((current) => {
      const next = new Set(current);
      if (next.has(moduleOrder)) next.delete(moduleOrder);
      else next.add(moduleOrder);
      return next;
    });
  };

  return (
    <Box component="nav" aria-label={copy.navigation} className="manual-navigation">
      <ManualSearch copy={copy} onNavigate={onNavigate} />
      <Divider />
      <List>
        <ListItemButton component={Link} to="/manual/" selected={!selectedSlug} onClick={onNavigate}>
          <ListItemText primary={copy.manualHome} />
        </ListItemButton>
        {manifest.modules.map((module) => {
          const expanded = expandedModules.has(module.navigation_order);
          const listId = `manual-module-${module.navigation_order}-articles`;
          return (
            <Box key={module.name} component="li" className="manual-module">
              <ListItemButton
                component="button"
                type="button"
                className="manual-module-toggle"
                aria-expanded={expanded}
                aria-controls={listId}
                onClick={() => toggleModule(module.navigation_order)}
              >
                <Typography component="span" className="manual-module-title">
                  {module.name}
                </Typography>
                <ExpandMoreIcon className="manual-module-chevron" fontSize="small" aria-hidden="true" />
              </ListItemButton>
              <List id={listId} disablePadding sx={{ display: expanded ? undefined : 'none' }}>
                {module.article_slugs.map((slug) => {
                  const article = bySlug.get(slug);
                  if (!article) return null;
                  return (
                    <ListItemButton
                      key={slug}
                      component={Link}
                      to={`/manual/${slug}`}
                      selected={selectedSlug === slug}
                      aria-current={selectedSlug === slug ? 'page' : undefined}
                      onClick={onNavigate}
                      className="manual-article-link"
                    >
                      <ListItemText primary={article.title} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </List>
    </Box>
  );
}

function WarningBanners({ warningIds, warningById }) {
  const groups = { error: [], warning: [], info: [] };
  warningIds.forEach((warningId) => {
    if (EDITORIAL_WARNING_IDS.has(warningId)) return;
    const warning = warningById.get(warningId);
    if (!warning) return;
    if (warning.severity === 'critical' || warning.severity === 'high') groups.error.push(warning);
    else if (warning.severity === 'caution') groups.warning.push(warning);
    else groups.info.push(warning);
  });

  return (
    <Stack spacing={1.5} className="manual-warning-stack">
      {Object.entries(groups).map(([severity, warnings]) => warnings.length > 0 && (
        <Alert
          key={severity}
          severity={severity}
          role={severity === 'error' ? 'alert' : 'status'}
          variant="outlined"
        >
          {warnings.map((warning) => (
            <Box key={warning.id} className="manual-warning-item">
              <strong>{warning.title}</strong>
              <span>{warning.explanation}</span>
            </Box>
          ))}
        </Alert>
      ))}
    </Stack>
  );
}

function LandingPage({ copy, manifest, onNavigate }) {
  const bySlug = new Map(manifest.articles.map((article) => [article.slug, article]));
  return (
    <main id="manual-main" tabIndex="-1" className="manual-main manual-landing">
      <Typography component="h1" variant="h3">
        {manifest.manual_title}
      </Typography>
      <Typography className="manual-lead">
        {copy.landingLead}
      </Typography>
      {manifest.modules.map((module) => (
        <section key={module.name} aria-labelledby={`module-${module.navigation_order}`}>
          <Typography component="h2" variant="h5" id={`module-${module.navigation_order}`}>
            {module.name}
          </Typography>
          <Box className="manual-card-grid">
            {module.article_slugs.map((slug) => {
              const article = bySlug.get(slug);
              if (!article) return null;
              return (
                <Card key={slug} variant="outlined">
                  <CardActionArea component={Link} to={`/manual/${slug}`} onClick={onNavigate}>
                    <CardContent>
                      <Typography component="h3" variant="h6">{article.title}</Typography>
                      <Typography variant="body2">{article.summary}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </section>
      ))}
    </main>
  );
}

function ManualNotFound({ copy }) {
  return (
    <main id="manual-main" tabIndex="-1" className="manual-main manual-state" role="status">
      <Typography component="h1" variant="h4">{copy.notFoundTitle}</Typography>
      <Typography>{copy.notFoundMessage}</Typography>
      <Button component={Link} to="/manual/" variant="contained">{copy.backToHome}</Button>
    </main>
  );
}

function ArticlePage({ article, copy, descriptor, manifest, onNavigate }) {
  const bySlug = useMemo(
    () => new Map(manifest.articles.map((item) => [item.slug, item])),
    [manifest.articles],
  );
  const warningById = useMemo(
    () => new Map(manifest.warnings.map((warning) => [warning.id, warning])),
    [manifest.warnings],
  );
  const headings = useMemo(
    () => parseHeadings(prepareMarkdown(article.markdown_body)),
    [article.markdown_body],
  );
  const reviewedDate = article.last_runtime_verification_date || article.last_editorial_review_date;
  const readerStatus = article.reader_status === 'Draft' ? null : article.reader_status;

  return (
    <main id="manual-main" tabIndex="-1" className="manual-main">
      <article className="manual-article">
        <Breadcrumbs aria-label="Manual breadcrumbs" className="manual-no-print">
          <MuiLink component={Link} underline="hover" to="/manual/">{copy.manual}</MuiLink>
          <Typography>{descriptor.parent_module}</Typography>
          <Typography color="text.primary">{article.title}</Typography>
        </Breadcrumbs>
        <Box className="manual-article-heading">
          <Box>
            <Typography component="h1" variant="h3">{article.title}</Typography>
            <Typography className="manual-summary">{article.summary}</Typography>
          </Box>
          {article.printable && (
            <Button
              className="manual-no-print"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              variant="outlined"
            >
              {copy.printArticle}
            </Button>
          )}
        </Box>
        {(readerStatus || reviewedDate) && (
          <Stack direction="row" spacing={1} className="manual-status-row" flexWrap="wrap">
            {readerStatus && <Chip label={readerStatus} variant="outlined" />}
            {reviewedDate && <Chip label={`${copy.lastReviewed} ${reviewedDate}`} variant="outlined" />}
          </Stack>
        )}
        <WarningBanners
          warningIds={article.applicable_reader_warning_ids}
          warningById={warningById}
        />
        <ArticleTableOfContents
          headings={headings}
          label={copy.onThisPage}
          navigationLabel={copy.articleContents}
        />
        <MarkdownArticle
          markdown={article.markdown_body}
          onInternalNavigate={onNavigate}
          labels={{
            external: copy.external,
            imageUnavailable: copy.imageUnavailable,
            tableScrollable: copy.tableScrollable,
          }}
        />

        {article.related_published_slugs.length > 0 && (
          <section className="manual-related manual-no-print" aria-labelledby="manual-related-title">
            <Typography component="h2" variant="h5" id="manual-related-title">{copy.relatedArticles}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {article.related_published_slugs.map((slug) => {
                const related = bySlug.get(slug);
                return related ? (
                  <Button key={slug} component={Link} to={`/manual/${slug}`} onClick={onNavigate}>
                    {related.title}
                  </Button>
                ) : null;
              })}
            </Stack>
          </section>
        )}
        <Box className="manual-pager manual-no-print">
          {article.previous_published_slug && bySlug.has(article.previous_published_slug) ? (
            <Button component={Link} to={`/manual/${article.previous_published_slug}`} onClick={onNavigate}>
              {copy.previous}: {bySlug.get(article.previous_published_slug).title}
            </Button>
          ) : <span />}
          {article.next_published_slug && bySlug.has(article.next_published_slug) && (
            <Button component={Link} to={`/manual/${article.next_published_slug}`} onClick={onNavigate}>
              {copy.next}: {bySlug.get(article.next_published_slug).title}
            </Button>
          )}
        </Box>
      </article>
    </main>
  );
}

export default function ManualViewer() {
  const wildcard = useParams()['*'] || '';
  const slug = wildcard.replace(/\/$/, '');
  const routeIsArticle = Boolean(slug) && !slug.includes('/') && SLUG_RE.test(slug);
  const routeIsLanding = !slug;
  const [colorMode, setColorMode] = useState(
    () => storedChoice('kalopsiaManualColorMode', ['light', 'dark'], 'light'),
  );
  const manualTheme = useMemo(() => createTheme({
    palette: {
      mode: colorMode,
      primary: { main: colorMode === 'dark' ? '#90caf9' : '#1e5f9f' },
      background: {
        default: colorMode === 'dark' ? '#101827' : '#f5f7fa',
        paper: colorMode === 'dark' ? '#182235' : '#ffffff',
      },
    },
  }), [colorMode]);
  const compact = useMediaQuery(manualTheme.breakpoints.down('md'));
  const copy = UI_COPY.en;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [manifest, setManifest] = useState(null);
  const [manifestState, setManifestState] = useState('loading');
  const [article, setArticle] = useState(null);
  const [articleState, setArticleState] = useState('idle');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    try {
      localStorage.setItem('kalopsiaManualColorMode', colorMode);
    } catch (_error) {
      // The selected mode still applies for the current tab when storage is unavailable.
    }
  }, [colorMode]);

  useEffect(() => {
    const clearProtectedState = () => {
      setDrawerOpen(false);
      setManifest(null);
      setArticle(null);
      setManifestState('session-expired');
      setArticleState('idle');
    };
    const clearIfSessionIsGone = () => {
      if (!hasStoredAccessToken()) clearProtectedState();
    };
    const handleStorage = (event) => {
      if (!event || event.key === 'apiUser') clearIfSessionIsGone();
    };

    window.addEventListener('kalopsia:session-expired', clearProtectedState);
    window.addEventListener('pageshow', clearIfSessionIsGone);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('kalopsia:session-expired', clearProtectedState);
      window.removeEventListener('pageshow', clearIfSessionIsGone);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setManifestState('loading');
    getManualManifest()
      .then((response) => {
        if (!active) return;
        if (!response || !Array.isArray(response.articles) || !Array.isArray(response.modules)) {
          throw new Error('invalid-content');
        }
        setManifest(response);
        setManifestState('ready');
      })
      .catch((error) => {
        if (!active) return;
        setManifest(null);
        setManifestState(error.kind || 'invalid-content');
      });
    return () => {
      active = false;
      setManifest(null);
      setArticle(null);
    };
  }, []);

  const descriptor = manifest?.articles?.find((item) => item.slug === slug);
  useEffect(() => {
    let active = true;
    setArticle(null);
    if (!manifest || !routeIsArticle || !descriptor) {
      setArticleState('idle');
      return () => { active = false; };
    }
    setArticleState('loading');
    getManualArticle(slug)
      .then((response) => {
        if (!active) return;
        setArticle(response);
        setArticleState('ready');
      })
      .catch((error) => {
        if (!active) return;
        setArticleState(error.kind || 'unavailable');
      });
    return () => {
      active = false;
      setArticle(null);
    };
  }, [manifest, routeIsArticle, descriptor, slug]);

  if (manifestState === 'loading') {
    return (
      <ThemeProvider theme={manualTheme}>
        <Box
          className={`manual-root manual-theme-${colorMode}`}
          data-testid="manual-root"
          lang="en"
        >
          <LoadingState label={copy.loadingManual} />
        </Box>
      </ThemeProvider>
    );
  }
  if (manifestState !== 'ready') {
    return (
      <ThemeProvider theme={manualTheme}>
        <Box
          className={`manual-root manual-theme-${colorMode}`}
          data-testid="manual-root"
          lang="en"
        >
          <ErrorState kind={manifestState} />
        </Box>
      </ThemeProvider>
    );
  }

  const closeDrawer = () => setDrawerOpen(false);
  const drawer = (
    <Navigation
      copy={copy}
      manifest={manifest}
      selectedSlug={routeIsArticle ? slug : ''}
      onNavigate={closeDrawer}
    />
  );

  let content;
  if (routeIsLanding) {
    content = (
      <LandingPage copy={copy} manifest={manifest} onNavigate={closeDrawer} />
    );
  } else if (!routeIsArticle || !descriptor) {
    content = <ManualNotFound copy={copy} />;
  } else if (articleState === 'loading' || articleState === 'idle') {
    content = <LoadingState label={copy.loadingArticle} />;
  } else if (articleState !== 'ready' || !article) {
    content = articleState === 'not-found'
      ? <ManualNotFound copy={copy} />
      : <ErrorState kind={articleState} />;
  } else {
    content = (
      <ArticlePage
        article={article}
        copy={copy}
        descriptor={descriptor}
        manifest={manifest}
        onNavigate={closeDrawer}
      />
    );
  }

  return (
    <ThemeProvider theme={manualTheme}>
      <Box
        className={`manual-root manual-theme-${colorMode}`}
        data-testid="manual-root"
        lang="en"
      >
        <a href="#manual-main" className="manual-skip-link">{copy.skipToContent}</a>
        <AppBar
          position="fixed"
          className="manual-appbar manual-no-print"
          sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }}
        >
          <Toolbar sx={{ ml: compact ? 0 : `${DRAWER_WIDTH}px`, gap: { xs: 0.5, sm: 1 } }}>
            {compact && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setDrawerOpen(true)}
                aria-label={copy.openNavigation}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="div" noWrap className="manual-app-title">
              {copy.appTitle}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={colorMode === 'dark' ? copy.lightMode : copy.darkMode}>
              <FormControlLabel
                className="manual-theme-control"
                control={(
                  <Switch
                    checked={colorMode === 'dark'}
                    color="default"
                    inputProps={{ 'aria-label': colorMode === 'dark' ? copy.lightMode : copy.darkMode }}
                    onChange={(event) => setColorMode(event.target.checked ? 'dark' : 'light')}
                    size="small"
                  />
                )}
                label={colorMode === 'dark'
                  ? <DarkModeIcon aria-hidden="true" fontSize="small" />
                  : <LightModeIcon aria-hidden="true" fontSize="small" />}
              />
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Box component="aside" className="manual-no-print">
          <Drawer
            variant={compact ? 'temporary' : 'permanent'}
            open={compact ? drawerOpen : true}
            onClose={closeDrawer}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: { width: DRAWER_WIDTH },
              'aria-label': copy.navigationDrawer,
            }}
          >
            {drawer}
          </Drawer>
        </Box>
        <Box className="manual-content" sx={{ ml: compact ? 0 : `${DRAWER_WIDTH}px` }}>
          {content}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
